import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'
import type { WerkbonConflictDTO } from './werkbonnen'

// ─── Enums ───────────────────────────────────────────────────────────────────

export const KALENDER_EVENT_SCOPE = ['BEDRIJFSBREED', 'TEAM', 'PERSOONLIJK', 'MEETING'] as const
export type KalenderEventScope = (typeof KALENDER_EVENT_SCOPE)[number]

export type AttendeeResponseStatus = 'NONE' | 'ACCEPTED' | 'TENTATIVE' | 'DECLINED'

export const ATTENDEE_RESPONSE_LABELS: Record<AttendeeResponseStatus, string> = {
  NONE: 'Geen reactie',
  ACCEPTED: 'Geaccepteerd',
  TENTATIVE: 'Misschien',
  DECLINED: 'Afgewezen',
}

export interface KalenderAttendeeDTO {
  id: string
  employeeId: string | null
  email: string
  responseStatus: AttendeeResponseStatus
  isOptional: boolean
  displayName?: string
}

export type OutlookSyncJobType =
  | 'PUSH_EVENT'
  | 'DELETE_EVENT'
  | 'IMPORT_DELTA'
  | 'INITIAL_SYNC'
  | 'RENEW_SUBSCRIPTION'

export type OutlookSyncJobStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED'

export interface OutlookSyncStateDTO {
  userId: string
  streepCalendarId: string | null
  primarySubscriptionExpires: string | null
  streepSubscriptionExpires: string | null
  initialSyncCompletedAt: string | null
  lastSyncedAt: string | null
  syncError: string | null
  syncErrorAt: string | null
  syncDisabled: boolean
}

export const KALENDER_EVENT_TYPE = [
  'ALGEMEEN',
  'TRAINING',
  'MEETING',
  'EVENT',
  'FEESTDAG',
  'HERINNERING',
] as const
export type KalenderEventType = (typeof KALENDER_EVENT_TYPE)[number]

// ─── Locatie (Spec NN §3.2.1B — PO-besluit 11) ────────────────────────────────
//
// Discriminator voor de polymorphic kalender-locatie. ENTITY/CLIENT_LOCATION/
// OPLEIDER_LOCATIE wijzen naar een bestaande locatie (geen dubbele invoer);
// CUSTOM gebruikt de losse adres/lat/lng-velden; ONLINE laat alles leeg
// (geen reistijd).

export const KALENDER_LOCATIE_TYPE = [
  'ENTITY',
  'CLIENT_LOCATION',
  'OPLEIDER_LOCATIE',
  'ONLINE',
  'CUSTOM',
] as const
export type KalenderLocatieType = (typeof KALENDER_LOCATIE_TYPE)[number]

export const KALENDER_LOCATIE_TYPE_LABELS: Record<KalenderLocatieType, string> = {
  ENTITY:           'Eigen vestiging',
  CLIENT_LOCATION:  'Klantlocatie',
  OPLEIDER_LOCATIE: 'Opleider-locatie',
  ONLINE:           'Online',
  CUSTOM:           'Ander adres',
}

// ─── Recurrence ──────────────────────────────────────────────────────────────

export const RECURRENCE_FREQ = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'] as const
export type RecurrenceFreq = (typeof RECURRENCE_FREQ)[number]

export const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const
export type WeekdayCode = (typeof WEEKDAY_CODES)[number]

export const SERIES_SCOPE = ['THIS', 'THIS_AND_FUTURE', 'ALL'] as const
export type SeriesScope = (typeof SERIES_SCOPE)[number]

/** Harde bovengrens op voorkomens per reeks (anti-runaway). */
export const MAX_RECURRENCE_OCCURRENCES = 400

export type RecurrenceEnd =
  | { type: 'UNTIL'; date: string }   // yyyy-MM-dd, inclusief
  | { type: 'COUNT'; count: number }

export interface RecurrenceRule {
  freq: RecurrenceFreq
  interval: number
  byWeekday?: WeekdayCode[]
  end: RecurrenceEnd
}

// ─── Labels ──────────────────────────────────────────────────────────────────

export const KALENDER_EVENT_SCOPE_LABELS: Record<KalenderEventScope, string> = {
  BEDRIJFSBREED: 'Bedrijfsbreed',
  TEAM:          'Team',
  PERSOONLIJK:   'Persoonlijk',
  MEETING:       'Vergadering',
}

export const KALENDER_EVENT_TYPE_LABELS: Record<KalenderEventType, string> = {
  ALGEMEEN:    'Algemeen',
  TRAINING:    'Training',
  MEETING:     'Meeting',
  EVENT:       'Event',
  FEESTDAG:    'Feestdag',
  HERINNERING: 'Herinnering',
}

// Kleurklassen voor de UI (Tailwind safe-list).
// Houd deze in sync met de planning-styles per type.
export const KALENDER_EVENT_TYPE_COLOR: Record<
  KalenderEventType,
  'blue' | 'amber' | 'purple' | 'green' | 'red' | 'gray'
> = {
  ALGEMEEN:    'gray',
  TRAINING:    'purple',
  MEETING:     'blue',
  EVENT:       'amber',
  FEESTDAG:    'red',
  HERINNERING: 'green',
}

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface KalenderEventDTO {
  id: string
  scope: KalenderEventScope
  type: KalenderEventType
  departmentId: string | null
  departmentName: string | null
  employeeId: string | null
  employeeName: string | null
  titel: string
  beschrijving: string | null
  locatie: string | null
  startTijd: string // ISO
  eindTijd: string // ISO
  isAllDay: boolean
  geplaatstDoor: string // userId
  createdAt: string
  updatedAt: string
  organizerId: string | null
  organizerName: string | null
  organizerEmail: string | null
  attendees: KalenderAttendeeDTO[]
  seriesId: string | null
  recurrence: RecurrenceRule | null
  /**
   * Spec NN §3.2.1B — polymorphic locatie. `null` als het event (nog) geen
   * gestructureerde locatie heeft (alleen het vrije `locatie`-label). De UI
   * mapt dit 1:1 op de `LocatiePicker`-waarde.
   */
  locatieRef: KalenderEventLocatieDTO | null
  /**
   * Spec NN §3.2.1C — aantal interne werkbonnen dat uit dit event is
   * gegenereerd (`Werkbon.sourceKalenderEventId`, niet soft-deleted). Gebruikt
   * voor de "X werkbonnen gekoppeld"-badge in de detail-modal. Alleen gevuld op
   * de detail-GET; in de lijst-respons `0`.
   */
  gekoppeldeWerkbonnen: number
  /** FO-008 — is dit een besloten afspraak? (slotje-markering voor wie het mag zien.) */
  besloten: boolean
  /**
   * FO-008 — is de inhoud AFGESCHERMD voor de huidige viewer? `true` = de server
   * gaf alleen een neutraal "bezet"-blok terug (`titel` = "Besloten", overige
   * inhoud/deelnemers leeg of null); niet te openen (detail-endpoint geeft 403).
   * Server-side bepaald (CC.SCOPE), nooit client-side.
   */
  afgeschermd: boolean
  /**
   * FO-008 — de door de maker gekozen medewerkers (Employee.id's). Alleen gevuld
   * voor wie de inhoud mag zien (maker/gekozen/attendee); leeg (`[]`) wanneer
   * `afgeschermd`. De maker beheert deze lijst.
   */
  beslotenEmployeeIds: string[]
}

/**
 * Een verlof-overlay block in de kalender-view. Wordt niet als KalenderEvent
 * opgeslagen — leeft in LeaveRequest. We mappen alleen de zichtbare velden.
 */
export interface KalenderVerlofOverlayDTO {
  kind: 'VERLOF'
  id: string // LeaveRequest.id
  employeeId: string
  employeeName: string
  startTijd: string
  eindTijd: string
  isAllDay: boolean
  titel: string // bv. "Verlof — Jan Jansen"
  status: string // LeaveStatus enum als string
}

/**
 * Werkbon-planning overlay in de kalender-view. Komt uit WerkbonDayEntry.
 */
export interface KalenderWerkbonOverlayDTO {
  kind: 'WERKBON'
  id: string // WerkbonDayEntry.id
  werkbonId: string
  employeeId: string | null
  employeeName: string | null
  startTijd: string
  eindTijd: string
  isAllDay: boolean
  titel: string // bv. "WB-2026-001 — Klant X"
  clientName: string | null
  /** FO-008 — besloten werkbon (slotje-markering voor wie het mag zien). */
  besloten: boolean
  /**
   * FO-008 — afgeschermd voor de huidige viewer? `true` = `titel` = "Besloten",
   * `clientName` = null; de tijd-/bezet-info blijft zodat capaciteit klopt.
   */
  afgeschermd: boolean
}

export type KalenderOverlayDTO = KalenderVerlofOverlayDTO | KalenderWerkbonOverlayDTO

export interface KalenderListResponse {
  events: KalenderEventDTO[]
  overlays: KalenderOverlayDTO[]
}

export interface KalenderFeedTokenDTO {
  id: string
  token: string
  scopes: KalenderEventScope[]
  laatstGebruikt: string | null
  createdAt: string
  feedUrl: string // bv. https://api.streepone.nl/api/v1/kalender/feed.ics?token=...
}

/**
 * Spec 1 A1.1 — conflicten van één medewerker over de gevraagde datums. De
 * `conflicts` zijn de platte `WerkbonConflictDTO`-lijst (één per (datum,
 * conflicttype)); een lege lijst betekent "geen conflicten".
 */
export interface KalenderEmployeeConflicts {
  employeeId: string
  employeeNaam: string
  conflicts: WerkbonConflictDTO[]
}

/** Response-body van POST /kalender/events/check-conflicts. */
export interface KalenderConflictCheckResult {
  conflicts: KalenderEmployeeConflicts[]
}

// ─── Zod request schemas ─────────────────────────────────────────────────────

export const recurrenceRuleSchema = z.object({
  freq: z.enum(RECURRENCE_FREQ),
  interval: z.number().int().min(1).max(99),
  byWeekday: z.array(z.enum(WEEKDAY_CODES)).min(1).max(7).optional(),
  end: z.discriminatedUnion('type', [
    z.object({ type: z.literal('UNTIL'), date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) }),
    z.object({
      type: z.literal('COUNT'),
      count: z.number().int().min(1).max(MAX_RECURRENCE_OCCURRENCES),
    }),
  ]),
})
export type RecurrenceRuleInput = z.infer<typeof recurrenceRuleSchema>

export const listKalenderQuerySchema = z.object({
  scope: z.enum(KALENDER_EVENT_SCOPE).optional(),
  vanaf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  totEnMet: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  employeeId: idParam().optional(),
  departmentId: idParam().optional(),
  /** Default true; portal kan via false voor lichtere requests */
  includeOverlays: queryBoolean().default(true).optional(),
  /**
   * Portal forceert dit altijd op true zodat de monteur alleen eigen verlof
   * en werkbon-overlays ziet — ook als de user óók backoffice + canApprove
   * heeft (HR/Admin die zichzelf in de portal bekijkt).
   */
  mineOnly: queryBoolean().optional(),
})

export type ListKalenderQuery = z.infer<typeof listKalenderQuerySchema>

export const attendeeSchema = z.object({
  employeeId: z.string().optional(),
  email: z.string().email(),
  isOptional: z.boolean().optional(),
})

export type AttendeeInput = z.infer<typeof attendeeSchema>

// ─── Polymorphic kalender-locatie schema (Spec NN §3.2.1B — PO-besluit 11) ────
//
// Gedefinieerd vóór de event-schemas zodat `baseEventSchema`/`updateEventSchema`
// het kunnen embedden. De bijbehorende DTO-interfaces staan verderop (type-level,
// dus volgorde maakt daar niet uit). lat/lng volgen de codebase-conventie
// `number` (Prisma `Decimal(9,6)`); optioneel want ONLINE heeft ze niet en een
// net-ingevoerde CUSTOM is nog niet ge-geocoded.

const latSchema = z.number().gte(-90).lte(90)
const lngSchema = z.number().gte(-180).lte(180)

/**
 * Zod-schema voor de polymorphic kalender-locatie. Dwingt de app-laag-
 * constraint af: precies één bron-FK per type, ONLINE leeg, CUSTOM met adres.
 */
export const kalenderEventLocatieSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('ENTITY'),
    entityId: idParam('entityId'),
    label: z.string().trim().min(1).max(200),
    lat: latSchema.nullable().optional(),
    lng: lngSchema.nullable().optional(),
  }),
  z.object({
    type: z.literal('CLIENT_LOCATION'),
    clientLocationId: idParam('clientLocationId'),
    label: z.string().trim().min(1).max(200),
    lat: latSchema.nullable().optional(),
    lng: lngSchema.nullable().optional(),
  }),
  z.object({
    type: z.literal('OPLEIDER_LOCATIE'),
    opleiderLocatieId: idParam('opleiderLocatieId'),
    label: z.string().trim().min(1).max(200),
    lat: latSchema.nullable().optional(),
    lng: lngSchema.nullable().optional(),
  }),
  z.object({
    type: z.literal('ONLINE'),
    label: z.string().trim().max(200).optional(),
  }),
  z.object({
    type: z.literal('CUSTOM'),
    adres: z.string().trim().min(1).max(300),
    lat: latSchema.nullable().optional(),
    lng: lngSchema.nullable().optional(),
    placeId: z.string().trim().max(500).nullable().optional(),
  }),
])

export type KalenderEventLocatieInput = z.infer<typeof kalenderEventLocatieSchema>

const baseEventSchema = {
  type: z.enum(KALENDER_EVENT_TYPE).default('ALGEMEEN'),
  titel: z.string().trim().min(1).max(200),
  beschrijving: z.string().trim().max(2000).optional(),
  // Vrije-tekst label (denormalized; ICS-feed + lijstweergave). Wordt door de
  // API automatisch afgeleid uit `locatieRef` als die meekomt, maar blijft los
  // toegestaan voor events zonder gestructureerde locatie.
  locatie: z.string().trim().max(200).optional(),
  // Spec NN §3.2.1B — gestructureerde locatie (polymorphic). Optioneel; als hij
  // meekomt zet de server de bron-FK's + leidt het `locatie`-label eruit af.
  locatieRef: kalenderEventLocatieSchema.optional(),
  startTijd: z.string().datetime(),
  eindTijd: z.string().datetime(),
  isAllDay: z.boolean().default(false),
  // FO-008 — besloten afspraak + de door de maker gekozen medewerkers (Employee.id's).
  // De route dwingt de beheer-permissie af (maker of KALENDER.canApprove) en seedt de
  // lijst met de attendees/employee als er geen expliciete keuze meekomt.
  besloten: z.boolean().default(false),
  beslotenEmployeeIds: z.array(idParam()).max(200).optional(),
}

/**
 * Discriminated union per scope: server kiest field-set op basis van scope.
 * - BEDRIJFSBREED: geen FK
 * - TEAM         : departmentId verplicht
 * - PERSOONLIJK  : employeeId verplicht
 * - MEETING      : attendees verplicht (min 1)
 */
export const createEventSchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('BEDRIJFSBREED'), ...baseEventSchema, recurrence: recurrenceRuleSchema.optional() }),
  z.object({
    scope: z.literal('TEAM'),
    departmentId: idParam(),
    ...baseEventSchema,
    recurrence: recurrenceRuleSchema.optional(),
  }),
  z.object({
    scope: z.literal('PERSOONLIJK'),
    // Beide optioneel — de route eist minstens één. `employeeId` houdt bestaande
    // single-callers werkend; `employeeIds` (min 1) opent de multi-medewerker-
    // modus: de route maakt dan één event met één attendee per medewerker, zodat
    // de werkbon-generator per medewerker een eigen werkbon (en planbord-entry)
    // genereert.
    employeeId: idParam().optional(),
    employeeIds: z.array(idParam()).min(1).optional(),
    // Spec 1 Deel 2 — meerdere-datums-modus. Een EXPLICIETE set NL-kalenderdagen
    // (geen regel zoals `recurrence`): elke datum krijgt een eigen event-rij met
    // dezelfde `seriesId`, zodat de bestaande serie-scope (THIS/THIS_AND_FUTURE/
    // ALL) op PATCH/DELETE er automatisch op werkt. De tijd-van-dag + duur komen
    // uit `startTijd`/`eindTijd`; per datum her-anchort de route die op de NL
    // wall-clock (DST-correct). Sluit `recurrence` uit (A1.5, afgedwongen in de
    // route). Primair voor de multi-medewerker-planning (combineert met
    // `employeeIds`), maar werkt ook met een enkele `employeeId`.
    dates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).min(1).optional(),
    // Spec 1 A1.3/A1.5 — uitgesloten (datum, medewerker)-cellen: voor díe datum
    // krijgt die medewerker géén attendee/werkbon. Een datum waarvan álle
    // medewerkers zijn uitgesloten, levert geen event-rij op (overgeslagen).
    excludePairs: z
      .array(z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), employeeId: idParam() }))
      .optional(),
    ...baseEventSchema,
    recurrence: recurrenceRuleSchema.optional(),
  }),
  z.object({
    scope: z.literal('MEETING'),
    attendees: z.array(attendeeSchema).min(1),
    ...baseEventSchema,
    recurrence: recurrenceRuleSchema.optional(),
  }),
])

export type CreateEventInput = z.infer<typeof createEventSchema>

export const updateEventSchema = z.object({
  type: z.enum(KALENDER_EVENT_TYPE).optional(),
  titel: z.string().trim().min(1).max(200).optional(),
  beschrijving: z.string().trim().max(2000).nullable().optional(),
  locatie: z.string().trim().max(200).nullable().optional(),
  // Spec NN §3.2.1B — gestructureerde locatie. `null` wist de locatie (terug
  // naar geen-bron); een waarde zet de bron-FK's + leidt het label af.
  locatieRef: kalenderEventLocatieSchema.nullable().optional(),
  startTijd: z.string().datetime().optional(),
  eindTijd: z.string().datetime().optional(),
  isAllDay: z.boolean().optional(),
  attendees: z.array(attendeeSchema).optional(),
  seriesScope: z.enum(SERIES_SCOPE).optional(),
  recurrence: recurrenceRuleSchema.nullable().optional(),
  // FO-008 — besloten aan/uit + de gekozen medewerkers wijzigen. Alleen de maker
  // of KALENDER.canApprove mag dit (server-side afgedwongen). `null` op
  // beslotenEmployeeIds = leeg de lijst.
  besloten: z.boolean().optional(),
  beslotenEmployeeIds: z.array(idParam()).max(200).nullable().optional(),
})

export type UpdateEventInput = z.infer<typeof updateEventSchema>

export const createFeedTokenSchema = z.object({
  scopes: z.array(z.enum(KALENDER_EVENT_SCOPE)).min(1),
})

export type CreateFeedTokenInput = z.infer<typeof createFeedTokenSchema>

// ─── Conflict-check (Spec 1 A1.1 — waarschuwen, nooit blokkeren) ──────────────
//
// Read-only voorvertoning vóór een multi-datum/multi-medewerker create: per
// (medewerker × datum) detecteren we VERLOF/ZIEK/FEESTDAG/ANDERE_WERKBON/
// PERSOONLIJK_EVENT-conflicten. Blokkeert NOOIT — de UI toont de conflicten en
// laat de gebruiker alsnog doorgaan. De server her-anchort de tijd-van-dag op de
// NL wall-clock per datum (zelfde conventie als de create).
export const kalenderConflictCheckSchema = z.object({
  employeeIds: z.array(idParam()).min(1),
  dates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).min(1),
  startTijd: z.string().datetime(),
  eindTijd: z.string().datetime(),
  isAllDay: z.boolean().default(false),
})

export type KalenderConflictCheckInput = z.infer<typeof kalenderConflictCheckSchema>

// ─── Polymorphic kalender-locatie (Spec NN §3.2.1B — PO-besluit 11) ───────────
//
// Discriminated union op `type`. Precies één bron-FK per locatie-soort; ONLINE
// is leeg; CUSTOM levert een vrij adres. lat/lng (geocode-coördinaten) volgen de
// codebase-conventie `number` (Prisma `Decimal(9,6)`; zie targeting-map). lat/
// lng zijn optioneel: ONLINE heeft ze niet en een net-ingevoerde CUSTOM is nog
// niet ge-geocoded.

export interface KalenderEventLocatieEntityDTO {
  type: 'ENTITY'
  entityId: string
  label: string
  lat: number | null
  lng: number | null
}

export interface KalenderEventLocatieClientLocationDTO {
  type: 'CLIENT_LOCATION'
  clientLocationId: string
  label: string
  lat: number | null
  lng: number | null
}

export interface KalenderEventLocatieOpleiderLocatieDTO {
  type: 'OPLEIDER_LOCATIE'
  opleiderLocatieId: string
  label: string
  lat: number | null
  lng: number | null
}

export interface KalenderEventLocatieOnlineDTO {
  type: 'ONLINE'
  label?: string
}

export interface KalenderEventLocatieCustomDTO {
  type: 'CUSTOM'
  adres: string
  lat: number | null
  lng: number | null
  placeId: string | null
}

export type KalenderEventLocatieDTO =
  | KalenderEventLocatieEntityDTO
  | KalenderEventLocatieClientLocationDTO
  | KalenderEventLocatieOpleiderLocatieDTO
  | KalenderEventLocatieOnlineDTO
  | KalenderEventLocatieCustomDTO

// `kalenderEventLocatieSchema` + `KalenderEventLocatieInput` staan hierboven
// (vóór `baseEventSchema`), zodat de create-/update-schemas ze kunnen embedden.
